import { useEffect, useRef, useState } from "react";

import {
  gisAvailable,
  useAuthConfig,
  useLogin,
} from "../lib/auth";


export default function SignInPage() {
  const cfg = useAuthConfig();
  const login = useLogin();
  const btnRef = useRef<HTMLDivElement>(null);
  const [gisReady, setGisReady] = useState<boolean>(gisAvailable());

  // GIS script in index.html loads async; poll up to ~10s for window.google.accounts.id.
  useEffect(() => {
    if (gisReady) return;
    let n = 0;
    const id = setInterval(() => {
      if (gisAvailable()) { setGisReady(true); clearInterval(id); }
      else if (++n > 100) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, [gisReady]);

  useEffect(() => {
    if (!gisReady || !cfg.data?.google_client_id || !btnRef.current) return;
    window.google!.accounts.id.initialize({
      client_id: cfg.data.google_client_id,
      hd: cfg.data.allowed_email_domain,
      callback: (resp) => login.mutate(resp.credential),
    });
    window.google!.accounts.id.renderButton(btnRef.current, {
      theme: "outline",
      size: "large",
      shape: "rectangular",
      text: "signin_with",
    });
  }, [gisReady, cfg.data, login]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900">
          <span className="text-sky-500">Interactive</span> LOPA
        </h1>
        <p className="mb-6 mt-1 text-sm text-slate-500">
          Sign in with your{" "}
          <span className="font-mono text-slate-700">
            @{cfg.data?.allowed_email_domain ?? "quvia.ai"}
          </span>{" "}
          Google account to continue.
        </p>

        {cfg.isLoading && (
          <p className="text-sm text-slate-500">Loading sign-in…</p>
        )}
        {cfg.isError && (
          <ErrorBox>
            Couldn't load sign-in config: {(cfg.error as Error).message}
          </ErrorBox>
        )}
        {cfg.data && !cfg.data.google_client_id && (
          <ErrorBox>
            Sign-in isn't configured on this server. The administrator
            needs to set <code>GOOGLE_CLIENT_ID</code>.
          </ErrorBox>
        )}

        {cfg.data?.google_client_id && (
          <>
            <div ref={btnRef} className="flex justify-center" />
            {!gisReady && (
              <p className="mt-3 text-center text-xs text-slate-400">
                Loading Google Sign-In…
              </p>
            )}
          </>
        )}

        {login.isError && (
          <ErrorBox>
            {(login.error as Error).message.includes("403")
              ? `Only @${cfg.data?.allowed_email_domain ?? "quvia.ai"} accounts can sign in.`
              : (login.error as Error).message}
          </ErrorBox>
        )}
      </div>
    </main>
  );
}


function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
      {children}
    </div>
  );
}
